import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, MessageSquare, Pin, CheckCircle2, X } from "lucide-react";

const TYPE_ICON = {
  new_reply: MessageSquare,
  pin: Pin,
  solution: CheckCircle2,
};

/**
 * Fase 13.2 · Dropdown de notificaciones del foro. Se coloca en el header
 * del sidebar del alumno. Polling cada 60 s para actualizar contador.
 */
export default function ForumNotifBell({ programBasePath = "/app/alumno/programas" }) {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = () => {
    api.get("/forums/notifications/me")
      .then((r) => { setNotifs(r.data.items || []); setUnread(r.data.unread_count || 0); })
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markAllRead = async () => {
    try {
      await api.post("/forums/notifications/read-all");
      load();
    } catch (_) {}
  };

  const markOneRead = async (id) => {
    try {
      await api.post(`/forums/notifications/${id}/read`);
      load();
    } catch (_) {}
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="forum-notif-bell"
        className="relative inline-flex items-center gap-1 p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-white/5"
        aria-label="Notificaciones del foro"
        title="Notificaciones del foro"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#F26A21] text-[10px] font-bold text-[#0B0B0B] flex items-center justify-center"
            data-testid="forum-notif-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed left-64 bottom-16 md:absolute md:left-full md:ml-2 md:bottom-0 md:top-auto w-80 max-w-[calc(100vw-2rem)] bg-[#0F172A] border border-gray-800 rounded-xl shadow-2xl overflow-hidden z-50"
          data-testid="forum-notif-dropdown"
        >
          <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
            <p className="text-sm font-semibold text-white">Foro · notificaciones</p>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                data-testid="mark-all-read-btn"
                className="text-[11px] text-[#93C5FD] hover:text-white underline underline-offset-2"
              >
                Marcar todas
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifs.length === 0 ? (
              <p className="p-6 text-center text-sm text-gray-500">Sin notificaciones.</p>
            ) : (
              notifs.map((n) => {
                const Icon = TYPE_ICON[n.type] || Bell;
                const to = n.thread_id
                  ? `${programBasePath.split("/programas")[0]}/foro/thread/${n.thread_id}`
                  : "#";
                return (
                  <Link
                    key={n.id}
                    to={to}
                    onClick={() => { markOneRead(n.id); setOpen(false); }}
                    data-testid={`forum-notif-item-${n.id}`}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-gray-800 last:border-b-0 hover:bg-white/5 transition-colors ${
                      n.is_read ? "" : "bg-[#B84A0C]/10"
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      n.type === "solution" ? "bg-emerald-500/20 text-emerald-300"
                        : n.type === "pin" ? "bg-purple-500/20 text-purple-300"
                        : "bg-[#3B82F6]/20 text-[#93C5FD]"
                    }`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white line-clamp-2">{n.message}</p>
                      <p className="mt-1 text-[10px] text-gray-500 font-mono">
                        {fmtRel(n.created_at)}
                      </p>
                    </div>
                    {!n.is_read && (
                      <span className="w-2 h-2 rounded-full bg-[#F26A21] flex-shrink-0 mt-2" />
                    )}
                  </Link>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function fmtRel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "hace segundos";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} d`;
}
